import { Link } from "react-router-dom";
import SEO, { orgSchema, faqSchema, personSchema, breadcrumbSchema, webPageSchema, coachListSchema, offerCatalogSchema, contactPageSchema, blogSchema } from "../../lib/seo";
import { LOCATIONS, IMG, trackPhoneClick } from "../../data/site";
import { GLOBAL_FAQS, COACHES } from "../../data/content";
import { ARTICLES } from "../../data/articles";
import { Reveal, Eyebrow, TrialButton, SectionShell, FAQBlock, NewsletterForm } from "../../components/shared";
import { ScheduleFilterable, MembershipCards, CoachCards, PrivateLessonForm } from "../../components/interactive";

const PageHero = ({ eyebrow, title, sub, children }) => (
  <section className="bg-[#0A0B0D] text-white pt-24 pb-16 grain relative">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="mt-3 font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none">{title}</h1>
        {sub && <p className="mt-4 max-w-2xl text-white/70">{sub}</p>}
        {children}
      </Reveal>
    </div>
  </section>
);

export const SchedulePage = () => (
  <>
    <SEO title="Class Schedule | Mezger Martial Arts at Hidden Gym Richardson & North Dallas"
      description="Filter the full Mezger Martial Arts class schedule by location, discipline, level, age group, and day. Richardson & North Dallas. Reserve a free trial spot."
      schemas={[orgSchema(), webPageSchema("Class Schedule", "Filterable Mezger Martial Arts class schedule for Richardson and North Dallas.", "/schedule"), breadcrumbSchema([["Home", "/"], ["Schedule", "/schedule"]])]} />
    <PageHero eyebrow="Schedule" title={<>Find Your <span className="text-neutral-400">Hour.</span></>}
      sub="Filter by location, discipline, level, age group, or day. Every class below is included in Hidden Gym membership." />
    <SectionShell className="bg-white">
      <ScheduleFilterable />
      <div className="mt-8"><TrialButton campaign="schedule-page" page="schedule" testId="schedule-page-trial-btn">Reserve Your Trial Spot</TrialButton></div>
    </SectionShell>
  </>
);

export const MembershipPage = () => (
  <>
    <SEO title="Membership | Mezger Martial Arts — Sign Up Through Hidden Gym"
      description="Mezger Martial Arts membership is a Hidden Gym membership: unlimited martial arts classes plus full gym access at Richardson & North Dallas. See current offers."
      schemas={[orgSchema(), offerCatalogSchema(), breadcrumbSchema([["Home", "/"], ["Membership", "/membership"]])]} />
    <PageHero eyebrow="Membership" title={<>One Membership.<br /><span className="text-neutral-400">The Whole Gym.</span></>}
      sub="Mezger Martial Arts is the martial arts program at Hidden Gym, so your membership is a Hidden Gym membership — unlimited classes plus the gym floor, strength equipment, and open mat. Signup and billing happen on Hidden Gym's site." />
    <SectionShell>
      <MembershipCards />
      <div className="mt-12 grid lg:grid-cols-[1fr_minmax(260px,340px)] gap-8 items-start">
        <Reveal className="bg-white border border-black/10 p-8">
          <h2 className="font-display font-black uppercase text-2xl">Why does signup go through Hidden Gym?</h2>
          <p className="mt-3 text-[#374151] leading-relaxed">Because you get more that way. One membership covers everything under the roof: every martial arts class Guy's team teaches, plus Hidden Gym's full training floor for strength and conditioning on your off days. One bill, both locations, the whole building. Questions? <Link to="/contact" className="underline hover:text-neutral-500">Contact us</Link> or ask at either front desk.</p>
        </Reveal>
        <Reveal delay={0.1}>
          <img src="/media/photos/bjj-community.webp" srcSet="/media/photos/bjj-community-600.webp 600w, /media/photos/bjj-community.webp 1200w" sizes="(min-width: 1024px) 340px, 100vw"
            width="1200" height="1800" loading="lazy" alt="Training partners in gis on the mats at Mezger Martial Arts"
            className="w-full aspect-[3/4] object-cover object-top border border-black/10" data-testid="membership-community-photo" />
          <p className="mt-3 font-mono text-xs uppercase tracking-wider text-[#5A6270]">Beginners train next to black belts — same mats, same coaches.</p>
        </Reveal>
      </div>
    </SectionShell>
  </>
);

export const CoachesPage = () => (
  <>
    <SEO title="Coaches | Mezger Martial Arts — Guy Mezger & Staff"
      description="Meet the Mezger Martial Arts coaching staff at Hidden Gym: head instructor Guy Mezger and his senior coaches in Muay Thai, boxing, BJJ, and wrestling."
      schemas={[personSchema(), orgSchema(), coachListSchema(COACHES), breadcrumbSchema([["Home", "/"], ["Coaches", "/coaches"]])]} />
    <PageHero eyebrow="The Coaches" title={<>Learn From People<br /><span className="text-neutral-400">Who Lived It.</span></>}
      sub="Tap any coach for their full story, credentials, and favorite class to teach." />
    <SectionShell><CoachCards /></SectionShell>
  </>
);

export const FAQsPage = () => (
  <>
    <SEO title="FAQs | Mezger Martial Arts at Hidden Gym"
      description="Everything people ask before their first class: experience, sparring, what to wear, kids ages, membership through Hidden Gym, locations, and training over 40."
      schemas={[faqSchema(GLOBAL_FAQS), orgSchema(), breadcrumbSchema([["Home", "/"], ["FAQs", "/faqs"]])]} />
    <PageHero eyebrow="FAQs" title={<>Asked Every Week.<br /><span className="text-neutral-400">Answered Straight.</span></>} />
    <SectionShell className="bg-white">
      <div className="max-w-3xl"><FAQBlock faqs={GLOBAL_FAQS} testPrefix="faqs-page" /></div>
      <div className="mt-10"><TrialButton campaign="faqs" page="faqs" testId="faqs-trial-btn">Still Curious? First Class Is Free</TrialButton></div>
    </SectionShell>
  </>
);

export const ContactPage = () => (
  <>
    <SEO title="Contact | Mezger Martial Arts at Hidden Gym Richardson & North Dallas"
      description="Contact Mezger Martial Arts at Hidden Gym: Richardson (855) 204-0004 · North Dallas (817) 730-6787. Private lesson inquiries and newsletter signup."
      schemas={[orgSchema(), contactPageSchema(), breadcrumbSchema([["Home", "/"], ["Contact", "/contact"]])]} />
    <PageHero eyebrow="Contact" title={<>Talk to <span className="text-neutral-400">a Human.</span></>}
      sub="Fastest path: call the location nearest you, or book a free trial and ask everything in person." />
    <SectionShell className="bg-white">
      <div className="grid md:grid-cols-2 gap-8">
        {LOCATIONS.map((l) => (
          <Reveal key={l.slug}>
            <div className="border border-black/10 p-8">
              <h2 className="font-display font-black uppercase text-2xl">{l.short}</h2>
              <p className="mt-2 text-[#374151] text-sm">{l.address}</p>
              <a href={l.phoneHref} onClick={() => trackPhoneClick(l.slug, l.phoneHref)} className="mt-2 inline-block font-display font-bold text-xl hover:text-neutral-500" data-testid={`contact-phone-${l.slug}`}>{l.phone}</a>
              <p className="mt-2 text-sm text-[#5A6270]">{l.hours}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-12 max-w-xl">
        <h2 className="font-display font-black uppercase text-2xl">Newsletter</h2>
        <p className="mt-2 text-[#374151] text-sm mb-4">Training tips, schedule changes, and events. No spam.</p>
        <NewsletterForm sourcePage="contact" />
      </Reveal>
    </SectionShell>
  </>
);

export const WaiverPage = () => (
  <>
    <SEO title="Waiver | Mezger Martial Arts at Hidden Gym"
      description="Review and sign the Mezger Martial Arts / Hidden Gym training waiver before your first class. Arrive 15 minutes early to complete it at the front desk."
      schemas={[orgSchema(), webPageSchema("Training Waiver", "Waiver requirements for training at Mezger Martial Arts at Hidden Gym.", "/waiver"), breadcrumbSchema([["Home", "/"], ["Waiver", "/waiver"]])]} />
    <PageHero eyebrow="Waiver" title={<>Before You <span className="text-neutral-400">Step on the Mat.</span></>} />
    <SectionShell className="bg-white">
      <Reveal className="max-w-3xl space-y-4 text-[#374151] leading-relaxed">
        <p>Every student — trial or member — signs a liability waiver before training. It takes two minutes at the front desk; arrive 15 minutes before your first class and the staff will walk you through it.</p>
        <p>Parents or legal guardians must sign for anyone under 18 and remain on site for kids trial classes.</p>
        <p className="bg-amber-100 text-amber-900 inline-block px-3 py-2 font-mono text-sm">[WAIVER DOCUMENT / E-SIGN EMBED — SUPPLY HIDDEN GYM'S CURRENT WAIVER SYSTEM LINK]</p>
        <div className="pt-4"><TrialButton campaign="waiver" page="waiver" testId="waiver-trial-btn" /></div>
      </Reveal>
    </SectionShell>
  </>
);

const BLOG_POSTS = ARTICLES;

export const BlogPage = () => (
  <>
    <SEO title="Blog | Mezger Martial Arts — Training Advice from Guy Mezger's Team"
      description="Training advice, beginner guides, and straight answers from the Mezger Martial Arts coaching staff at Hidden Gym in Richardson and North Dallas."
      schemas={[orgSchema(), blogSchema(), breadcrumbSchema([["Home", "/"], ["Blog", "/blog"]])]} />
    <PageHero eyebrow="Blog" title={<>Straight Talk<br /><span className="text-neutral-400">From the Mats.</span></>} />
    <SectionShell className="bg-white">
      <div className="grid md:grid-cols-3 gap-6">
        {BLOG_POSTS.map((b, i) => (
          <Reveal key={b.slug} delay={i * 0.08} className="h-full">
            <Link to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group flex flex-col h-full border border-black/10 hover:border-[#0A0B0D] transition-colors overflow-hidden">
              <div className="aspect-video overflow-hidden">
                <img src={b.image} alt={b.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <span className="font-mono text-[11px] uppercase tracking-widest text-neutral-400">{b.tag} · {b.readTime}</span>
                <h2 className="mt-2 font-display font-black uppercase text-xl leading-tight group-hover:text-neutral-500 transition-colors">{b.title}</h2>
                <p className="mt-3 text-sm text-[#374151] flex-1">{b.intro.slice(0, 140)}…</p>
                <p className="mt-4 text-sm font-bold group-hover:text-neutral-500 transition-colors">Read the article →</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </SectionShell>
  </>
);

export const PrivateLessonsSection = () => (
  <SectionShell className="bg-white" id="inquire">
    <div className="grid lg:grid-cols-2 gap-12 items-start">
      <Reveal>
        <Eyebrow>Private Lessons</Eyebrow>
        <h2 className="mt-2 font-display font-black uppercase text-3xl sm:text-4xl">Tell Us Your Goal.<br />We'll Match the Coach.</h2>
        <p className="mt-4 text-[#374151] leading-relaxed">This is the one thing that stays on guymezger.com: your inquiry goes straight to Hidden Gym staff, who follow up with coach availability, current rates, and scheduling at your preferred location.</p>
        <img src={IMG.privateLesson} alt="One-on-one private martial arts lesson" loading="lazy" className="mt-6 w-full aspect-video object-cover border border-black/10" />
      </Reveal>
      <Reveal delay={0.1}><PrivateLessonForm /></Reveal>
    </div>
  </SectionShell>
);
